import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { updatePassword } from '../lib/auth'
import { useAuth } from '../context/AuthContext'
import { showToast } from '../components/Toast'

// Landing for the Supabase reset-password link. When Supabase Auth redirects
// here it stamps a temporary recovery session on the URL — because
// `detectSessionInUrl: true` is set on the client, that session is picked
// up automatically. The user's job here: set a new password.

export default function ResetPassword() {
  const navigate = useNavigate()
  const { user, loading } = useAuth()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // If someone lands here without a recovery session and isn't logged in,
  // bounce them back to login. (Once loading resolves.)
  useEffect(() => {
    if (!loading && !user) {
      // Give Supabase a beat to consume the token before deciding.
      const t = setTimeout(() => {
        if (!user) navigate('/login', { replace: true })
      }, 400)
      return () => clearTimeout(t)
    }
  }, [loading, user, navigate])

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords don\'t match.')
      return
    }
    setSubmitting(true)
    try {
      await updatePassword(password)
      showToast('Password updated 🎉')
      navigate('/order', { replace: true })
    } catch (err) {
      setError(err.message || 'Could not update password.')
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-10">
      <div className="mb-6 text-center">
        <img
          src="/logo-ghost.png"
          alt=""
          aria-hidden="true"
          width="72"
          height="72"
          className="mx-auto mb-3 h-[72px] w-[72px] rounded-2xl shadow-[0_10px_30px_rgba(11,32,56,0.25)]"
        />
        <h1 className="font-bricolage text-[20px] font-extrabold text-navy">
          Set a new password
        </h1>
        <p className="mt-1 text-[13px] text-ink-2">
          Choose something you&apos;ll remember.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="w-full rounded-2xl border border-line bg-white p-5 shadow-sm"
      >
        <div>
          <label className="field-label">New password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            placeholder="At least 8 characters"
            className="field-input"
            required
            minLength={8}
          />
        </div>
        <div className="mt-3">
          <label className="field-label">Confirm password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            placeholder="Type it again"
            className="field-input"
            required
            minLength={8}
          />
        </div>

        {error && (
          <p className="mt-3 rounded-lg bg-red/10 px-3 py-2 text-[12px] font-bold text-red">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className={`btn-navy mt-4 ${submitting ? 'opacity-60' : ''}`}
        >
          {submitting ? 'Updating…' : 'Update password'}
        </button>

        <p className="mt-4 text-center text-[12px] text-ink-2">
          <Link to="/login" className="font-extrabold text-aqua">Back to log in</Link>
        </p>
      </form>
    </div>
  )
}
